import { TooltipItem } from 'chart.js'

import { formatDate } from '../../../../helpers/date'
import { UserLanguage } from '../../../account/types/User'
import { CatalogueMetricKey, formatActiveEvents, formatAdoptionPct, formatDensity } from './catalogueUtils'

// Formatting shared by the blueprint-family and event-group trend charts, so a
// value, a date or a hover reads identically whichever chart it is on.

// Parse a "yyyy-MM-dd" tick without `new Date(iso)` (UTC midnight, which can
// shift the calendar day in US timezones). Falls back to Date's parser for
// unexpected shapes.
export function parseIsoDateLocal(dateStr: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  if (!match) return new Date(dateStr)
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

// Hover label for one daily point. Carries the year: a 24-month window
// crosses two or three calendar years and "Aug 1" alone does not say which.
export function formatTrendsChartDate(dateStr: string, language: UserLanguage): string {
  return formatDate(parseIsoDateLocal(dateStr), { language, month: 'short', day: 'numeric', year: 'numeric' })
}

// Axis / hover label for one monthly bucket ("YYYY-MM" → "Aug 2026"). The
// charts are monthly, so the label names the month and nothing more: "Aug 1,
// 2026" on a monthly chart read as a single day's figure.
export function formatTrendsMonthLabel(month: string, language: UserLanguage): string {
  const match = /^(\d{4})-(\d{2})/.exec(month)
  if (!match) return month
  return formatDate(new Date(Number(match[1]), Number(match[2]) - 1, 1), { language, month: 'short', year: 'numeric' })
}

// Exact value in a hover / data table: adoption is a 0..1 ratio (whole-number
// %, the same as every other adoption surface), density a decimal (2dp, more for values that would round to zero),
// concurrent instances a whole count.
export function formatTrendsValue(metric: CatalogueMetricKey, value: number): string {
  if (metric === 'activeEvents') return formatActiveEvents(value)
  if (metric === 'density') return formatDensity(value)
  return formatAdoptionPct(value)
}

// Y-axis tick: same units as the values.
export function formatTrendsTick(metric: CatalogueMetricKey, value: number | string): string {
  const numeric = Number(value)
  if (metric === 'activeEvents') return formatActiveEvents(numeric)
  if (metric === 'density') return formatDensity(numeric)
  return formatAdoptionPct(numeric)
}

// What the hovered month holds beyond the plotted value, for the charts that can say
// more about it. `undefined` for a month a chart knows nothing extra about.
export interface TrendsTooltipMonth {
  // The month does not cover a whole calendar month: the first point of the axis is
  // clamped to the window start, the last runs only to today.
  partial?: boolean
  // Cohort games that ran the entity on any day of the month, each counted once: the
  // numerator of the plotted share.
  distinctGamesCount?: number
  // The cohort: the denominator of the plotted share.
  trackedGamesCount?: number
}

export interface TrendsTooltipCallbacksOptions {
  metric: CatalogueMetricKey
  // Extra facts about the hovered month, by x index. Omit to keep the plain tooltip.
  monthAt?: (dataIndex: number) => TrendsTooltipMonth | undefined
  // Localized copy. `partial` captions the month; `monthGames` states the games behind
  // the plotted share. Omit either to leave that line off.
  labels?: {
    partial?: string
    monthGames?: (counts: { count: number; tracked: number }) => string
  }
}

// Chart.js tooltip callbacks: `title` is the x label (the month, captioned when the
// month is partial), every dataset line is `Series: value`.
//
// The metric definition used to sit in the footer, but Chart.js does not wrap text and
// a full sentence overflowed the canvas; it now lives in the caption under the chart
// title instead. For the same reason the month's own game count is appended only when
// a single series is plotted — on a ten-line catalogue chart it would triple the
// tooltip's height, and the number belongs to one entity at a time anyway.
export function buildTrendsTooltipCallbacks({ metric, monthAt, labels }: TrendsTooltipCallbacksOptions) {
  return {
    title: (items: TooltipItem<'line'>[]) => {
      const label = items[0]?.label ?? ''
      const month = monthAt?.(items[0]?.dataIndex ?? -1)
      return month?.partial && labels?.partial ? `${label} · ${labels.partial}` : label
    },
    label: (context: TooltipItem<'line'>) => {
      const raw = typeof context.raw === 'number' ? context.raw : Number(context.raw)
      return `${context.dataset.label}: ${formatTrendsValue(metric, raw)}`
    },
    afterLabel: (context: TooltipItem<'line'>) => {
      if (metric !== 'adoption' || !labels?.monthGames) return undefined
      if (context.chart.data.datasets.length !== 1) return undefined
      const month = monthAt?.(context.dataIndex)
      if (month?.distinctGamesCount === undefined || !month.trackedGamesCount) return undefined
      return labels.monthGames({ count: month.distinctGamesCount, tracked: month.trackedGamesCount })
    },
  }
}
